/**
 * Who may press which downhole write — mirrors the API's role lists in
 * fieldiq-api `src/routes/downhole.routes.ts`, and is not the enforcement.
 *
 *   DOWNHOLE_INVENTORY_ROLES  component create, update and import (api#900).
 *                             Inventory is office work: a hand reads and builds
 *                             with the tools, they do not add them to the books.
 *   DOWNHOLE_BUILD_ROLES      the assembly builder, run logs, assembly delete and
 *                             template archive / restore (api#883, #902). A
 *                             field_worker is on this list.
 *
 * The API's `requireRole` is a plain membership test for these lists (the
 * dispatcher elevation only ever ADDS admin, and dispatcher is already on
 * both), so a membership test here gives the same answer. A button these
 * return false for is left off the screen rather than greyed out — the API
 * would answer 403.
 *
 * Kept out of `lib/roles.ts` on purpose: that file is imported across every
 * vertical, and these lists are the downhole routes' and move with them.
 */
export const DOWNHOLE_INVENTORY_ROLES = ['admin', 'office_staff', 'dispatcher'] as const;
export const DOWNHOLE_BUILD_ROLES = ['admin', 'office_staff', 'dispatcher', 'field_worker'] as const;

export function canManageDownholeInventory(role: string | null | undefined): boolean {
  return !!role && (DOWNHOLE_INVENTORY_ROLES as readonly string[]).includes(role);
}

export function canBuildDownhole(role: string | null | undefined): boolean {
  return !!role && (DOWNHOLE_BUILD_ROLES as readonly string[]).includes(role);
}

/**
 * Well jobs (api#1055, DOWNHOLE_JOB_WRITE_ROLES): the roles that book work,
 * admin, office_staff and dispatcher. A field_worker reads them only.
 */
export const DOWNHOLE_JOB_WRITE_ROLES = ['admin', 'office_staff', 'dispatcher'] as const;

export function canWriteDownholeJobs(role: string | null | undefined): boolean {
  return !!role && (DOWNHOLE_JOB_WRITE_ROLES as readonly string[]).includes(role);
}

/**
 * Who ASSIGNS shop work: the office (admin, office_staff, dispatcher). The API
 * lets every build role PATCH `assigned_to`, field_worker included, but
 * deciding who works an order is a dispatch call, not something a hand does
 * from the bench — so the control is the office's, and the field shell only
 * reads the assignee. Same three roles as inventory, named for what it means.
 */
export function canAssignDownholeWork(role: string | null | undefined): boolean {
  return canManageDownholeInventory(role);
}

/**
 * The office's work-order controls (api#905 PATCH): status moves, complete,
 * cancel, and the edit — priority, due day, description, findings,
 * resolution, labour and the parts list. The API takes these from every build
 * role; the field shell gives a hand Start / Waiting on parts / Complete on an
 * order assigned to them and nothing else, because the parts list carries a
 * cost and cancelling or re-prioritising is a shop-office call. Same three
 * roles as assigning.
 */
export function canManageDownholeWorkOrders(role: string | null | undefined): boolean {
  return canManageDownholeInventory(role);
}

/**
 * The rate card (api#906): read by admin, office_staff and dispatcher (who
 * quotes jobs); written by admin and office_staff only. A field hand does not
 * see rates.
 */
export function canReadDownholeRates(role: string | null | undefined): boolean {
  return role === 'admin' || role === 'office_staff' || role === 'dispatcher';
}

export function canWriteDownholeRates(role: string | null | undefined): boolean {
  return role === 'admin' || role === 'office_staff';
}

/**
 * Downhole invoicing (api#910, unified-invoices). Generate and send: admin and
 * office_staff. The PDF: those plus dispatcher. Void: admin ONLY — the API's
 * guard is strict, so a dispatcher with admin elevation is refused too.
 */
export function canWriteDownholeInvoices(role: string | null | undefined): boolean {
  return role === 'admin' || role === 'office_staff';
}

export function canReadDownholeInvoicePdf(role: string | null | undefined): boolean {
  return role === 'admin' || role === 'office_staff' || role === 'dispatcher';
}

export function canVoidDownholeInvoices(role: string | null | undefined): boolean {
  return role === 'admin';
}
